import { BaseGame } from './BaseGame';
import { GameConfig, PlayerReadyForSeating, Card } from '../types';
import { GameStates } from '../constants/GameStates';

/**
 * Texas Hold'em Poker game implementation
 * Extends BaseGame to allow for future expansion with other poker variants
 */
export class TexasHoldemGame extends BaseGame {
  private deck: Card[] = [];
  private mainPlayerUUID: string = '';
  private mainPlayerName: string = '';
  private roomId: string = '';

  constructor(config: GameConfig, gameId: string) {
    super(config, gameId);
    this.initializeDeck();
  }

  setMainInfo(mainPlayerUUID: string, mainPlayerName: string, roomId: string): void {
    this.mainPlayerUUID = mainPlayerUUID;
    this.mainPlayerName = mainPlayerName;
    this.gameState.roomId = roomId;
  }

  initiateGame(playersReadyForSeating: PlayerReadyForSeating[]): void {
    this.updateGameState(GameStates.GAME_INIT);
    this.setPlayers(playersReadyForSeating);
    this.initializeSeats();
    
    // Mark main player
    const mainPlayer = this.gameState.players.find(
      (p) => p.uuid === this.mainPlayerUUID
    );
    if (mainPlayer) {
      mainPlayer.isMainPlayer = true;
    }

    // Assign players to seats
    playersReadyForSeating.forEach((p) => {
      const seat = this.gameState.seats[p.seatIndex];
      if (seat) {
        seat.isOccupied = true;
        const player = this.gameState.players.find((pl) => pl.uuid === p.playerId);
        if (player) {
          seat.player = player;
        }
      }
    });
  }

  updateGameState(newState: GameStates): void {
    this.gameState.state = newState;
  }

  dealCards(): void {
    // Shuffle deck
    this.shuffleDeck();
    // Deal cards logic will be handled by server messages
  }

  processPlayerAction(playerId: string, action: string, amount?: number): void {
    const player = this.gameState.players.find((p) => p.uuid === playerId);
    if (!player) return;

    switch (action) {
      case 'FOLD':
        player.isFolded = true;
        break;
      case 'CALL':
      case 'CHECK':
        // Handle call/check
        break;
      case 'RAISE':
      case 'BET':
        if (amount !== undefined) {
          player.currentBet = amount;
        }
        break;
    }
  }

  allocateCards(cards: Card[]): void {
    // Allocate cards to players (from server)
    const mainPlayer = this.gameState.players.find((p) => p.isMainPlayer);
    if (mainPlayer && cards.length >= 2) {
      mainPlayer.hand = [cards[0], cards[1]];
    }
  }

  dealTableCards(cards: Card[]): void {
    this.gameState.tableCards = cards;
  }

  private initializeDeck(): void {
    const suits = ['s', 'h', 'd', 'c'];
    const ranks = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
    
    this.deck = [];
    for (const suit of suits) {
      for (const rank of ranks) {
        this.deck.push({ rank, suit, isFaceUp: false });
      }
    }
  }

  private shuffleDeck(): void {
    for (let i = this.deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.deck[i], this.deck[j]] = [this.deck[j], this.deck[i]];
    }
  }
}

